// ✅ UPDATED SignUpPage.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./SignUpPage.css";
import { GOOGLE_CLIENT_ID, API_BASE_URL, TWOFACTOR_API_KEY } from "../config";

// Save any existing problem in localStorage for the new user
const saveUserProblem = async (userId) => {
  const savedProblem = localStorage.getItem("user_problem");
  if (savedProblem) {
    try {
      console.log("[DEBUG] Saving user problem:", savedProblem);
      await axios.post(`${API_BASE_URL}/save-user-problem`, {
        user_id: userId,
        problem: savedProblem,
      });
    } catch (err) {
      console.error("[ERROR] Failed to save problem:", err);
    }
  }
};

const OTP_VALIDITY_SECONDS = 120; // 2 minutes

const SignUpPage = () => {
  const navigate = useNavigate();

  const [isMobileMode, setIsMobileMode] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);

  // Email/password states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Mobile/OTP states
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [step, setStep] = useState(0);

  // UI state
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // On load: check if already logged in and set up Google client
  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (userId) {
      console.log("[DEBUG] User already logged in, redirecting to /form");
      navigate("/form", { replace: true });
    }

    if (window.google && !tokenClient) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        callback: async (tokenResponse) => {
          try {
            console.log("[DEBUG] Google token received:", tokenResponse);
            const userInfo = await axios.get(
              "https://www.googleapis.com/oauth2/v3/userinfo",
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.access_token}`,
                },
              }
            );

            const { email, name } = userInfo.data;
            console.log("[DEBUG] Google user info:", email, name);

            const res = await axios.post(`${API_BASE_URL}/google-login`, {
              email,
              name,
            });

            const userId = res.data.user_id;
            localStorage.setItem("user_id", userId);
            localStorage.setItem("email", res.data.email || email);
            localStorage.setItem("name", res.data.name || name);

            window.dispatchEvent(new Event("storage"));
            await saveUserProblem(userId);

            navigate("/");
          } catch (err) {
            console.error("[ERROR] Google login failed:", err);
            alert("Google sign-in failed. Please try again.");
          }
        },
      });

      setTokenClient(client);
    }
  }, [navigate, tokenClient]);

  // OTP countdown
  useEffect(() => {
    if (!otpExpiry) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((otpExpiry - Date.now()) / 1000));
      setTimeLeft(diff);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [otpExpiry]);

  const formatTime = (totalSec) => {
    const m = Math.floor(totalSec / 60)
      .toString()
      .padStart(2, "0");
    const s = (totalSec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const triggerGoogleLogin = () => {
    if (tokenClient) {
      console.log("[DEBUG] Triggering Google login...");
      tokenClient.requestAccessToken();
    } else {
      console.warn("[WARN] Google client not ready");
      alert("Google client not ready. Please refresh the page.");
    }
  };

  // Email signup
  const handleEmailSignup = async () => {
    try {
      if (!email || !password) {
        alert("Please enter email and password.");
        return;
      }
      console.log("[DEBUG] Signing up via email:", email);
      const res = await axios.post(`${API_BASE_URL}/signup`, {
        email,
        password,
      });
      const userId = res.data.user_id;
      if (userId) {
        localStorage.setItem("user_id", userId);
        localStorage.setItem("email", res.data.email || email);
        window.dispatchEvent(new Event("storage"));
        await saveUserProblem(userId);
        navigate("/");
      }
    } catch (err) {
      console.error("[ERROR] Email signup failed:", err);
      alert(err?.response?.data?.detail || "Email signup failed.");
    }
  };

  // Normalize phone
  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  // Send OTP
  const sendOtp = async () => {
    try {
      if (!mobile) {
        alert("Enter mobile number");
        return;
      }
      setSending(true);

      const toSend = normalizePhone(mobile);
      if (!/^\d{12,15}$/.test(toSend)) {
        alert("Please enter a valid phone number (with country code).");
        setSending(false);
        return;
      }

      const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedOtp(otpCode);
      setOtp("");
      setOtpExpiry(Date.now() + OTP_VALIDITY_SECONDS * 1000);
      setStep(1);

      console.log("[DEBUG] Generated OTP:", otpCode);

      // 🔹 Call 2Factor SMS API
      const url = `https://2factor.in/API/V1/${TWOFACTOR_API_KEY}/SMS/${encodeURIComponent(
        toSend
      )}/${otpCode}/OTP1`;

      await axios.get(url);

      alert("OTP sent to your mobile number!");
    } catch (err) {
      console.error("[ERROR] Failed to send OTP:", err);
      alert(
        err?.response?.data?.Details ||
          "Failed to send OTP. Please check number or try again."
      );
      setStep(0);
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async () => {
    if (timeLeft > 0) return;
    setGeneratedOtp("");
    setOtp("");
    setOtpExpiry(0);
    setTimeLeft(0);
    await sendOtp();
  };

  // ✅ Verify OTP + call backend
  const verifyOtpAndSignup = async () => {
    try {
      if (!otp) {
        alert("Enter OTP");
        return;
      }
      if (Date.now() > otpExpiry) {
        alert("OTP expired. Please request a new one.");
        setStep(0);
        return;
      }
      if (otp !== generatedOtp) {
        alert("Invalid OTP. Please try again.");
        return;
      }

      setVerifying(true);

      // 🔹 Call FastAPI endpoint (login or create)
      const res = await axios.post(`${API_BASE_URL}/user/login_or_create`, {
        mobile: normalizePhone(mobile),
        name: "New User", // optional: only required if new user
      });

      console.log("[DEBUG] Backend response:", res.data);

      const userId = res.data.user_id;
      if (userId) {
        localStorage.setItem("user_id", userId);
        localStorage.setItem("mobile", normalizePhone(mobile));
        if (res.data.name) localStorage.setItem("name", res.data.name);
        if (res.data.email) localStorage.setItem("email", res.data.email);
        window.dispatchEvent(new Event("storage"));
        await saveUserProblem(userId);
        navigate("/");
      } else {
        alert("Login failed. Please try again.");
      }
    } catch (err) {
      console.error(
        "[ERROR] OTP verification/login failed:",
        err?.response?.data || err?.message || err
      );
      alert(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-box">
        <h2 className="signup-title">Create an account</h2>

        {/* Email signup flow */}
        {!isMobileMode ? (
          <>
            <input
              type="email"
              className="signup-input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <input
              type="password"
              className="signup-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button
              className="signup-button"
              onClick={handleEmailSignup}
              disabled={!email || !password}
            >
              Continue with Email
            </button>
          </>
        ) : (
          <>
            {step === 0 ? (
              <>
                <input
                  type="tel"
                  className="signup-input"
                  placeholder="Mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel"
                />
                <button
                  className="signup-button"
                  onClick={sendOtp}
                  disabled={sending || !mobile}
                >
                  {sending ? "Sending..." : "Send OTP"}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  className="signup-input"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                />
                {timeLeft > 0 ? (
                  <p>⏳ OTP valid for {formatTime(timeLeft)}</p>
                ) : (
                  <p style={{ color: "red" }}>OTP expired.</p>
                )}

                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button
                    className="signup-button"
                    onClick={resendOtp}
                    disabled={sending || timeLeft > 0}
                  >
                    {sending ? "Resending..." : "Resend OTP"}
                  </button>
                  <button
                    className="signup-button secondary"
                    onClick={() => {
                      setStep(0);
                      setOtp("");
                      setGeneratedOtp("");
                      setOtpExpiry(0);
                      setTimeLeft(0);
                    }}
                  >
                    Change Number
                  </button>
                </div>

                <button
                  className="signup-button"
                  onClick={verifyOtpAndSignup}
                  disabled={verifying || !otp || timeLeft === 0}
                >
                  {verifying ? "Verifying..." : "Verify & Continue"}
                </button>
              </>
            )}
          </>
        )}

        <div className="divider">or</div>

        <button onClick={triggerGoogleLogin} className="google-button">
          <img
            src="https://www.svgrepo.com/show/355037/google.svg"
            alt="Google"
            className="google-icon"
          />
          Continue with Google
        </button>

        {!isMobileMode ? (
          <button
            className="google-button"
            onClick={() => {
              console.log("[DEBUG] Switching to mobile signup");
              setIsMobileMode(true);
            }}
          >
            📱 Continue with Mobile Number
          </button>
        ) : (
          <button
            className="signup-button secondary"
            onClick={() => {
              console.log("[DEBUG] Switching to email signup");
              setIsMobileMode(false);
              setStep(0);
              setOtp("");
              setGeneratedOtp("");
              setOtpExpiry(0);
              setTimeLeft(0);
            }}
          >
            ← Back to Email Signup
          </button>
        )}

        <div className="signup-footer">
          Already have an account?{" "}
          <Link to="/signin" className="signup-link">
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
